import { MutableRefObject, useRef, useState } from 'react';

import { IManageForm, ValidatorFunction, MultiFieldValidatorResult } from '@belong/types';

export type IUseFormErrors = { hasError: boolean; hasFormError: boolean; hasInputError: boolean };

export interface IUseForm {
  /**
   * An object containing the following boolean flags:
   *
   * `hasError`: If either `hasFormError` or `hasInputError` is true, this will return true, otherwise false
   *
   * `hasFormError`: Will return the value based on whether `addFormInError` or `removeFormInError` have been called
   *
   * `hasInputError`: If there are any inputs which are invalid. This is tracked based on the input components calling `addInputError` and `removeInputError`
   */
  errors: IUseFormErrors;

  /**
   * All form helper functions used to manage the form state. See below for more details on each item
   */
  manageForm: IManageForm;

  /**
   * A ref to be passed through to the `form` element
   */
  FormRef: MutableRefObject<HTMLFormElement | null>;

  /**
   * If `manageForm.setSubmitted` has been called it will return the value that has been set, defaults to `false`
   */
  hasSubmitted: boolean;

  /**
   * If `manageForm.toggleDisableButton` has been called it will return the value that has been set, defaults to `false`
   */
  buttonDisabled: boolean;
}

export const useForm = (): IUseForm => {
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);
  const [hasFormError, setFormError] = useState<boolean>(false);
  const [inputErrors, setInputError] = useState<string[]>([]);
  const [buttonDisabled, setButtonDisabledStatus] = useState<boolean>(false);
  const [multiFieldError, setMultiFieldError] = useState<MultiFieldValidatorResult>();
  const inputValidators = useRef<Record<string, ValidatorFunction>>({});
  const customValueGetter = useRef<Record<string, () => string>>({});
  const FormRef = useRef<HTMLFormElement | null>(null);
  const hasInputError = inputErrors.length > 0;
  const hasError = hasFormError || hasInputError || false;
  const errors = { hasError, hasFormError, hasInputError };

  const addFormInError = () => setFormError(() => true);
  const removeFormInError = () => setFormError(() => false);
  const addInputError = (name: string) => setInputError(prevState => [...prevState, name]);
  const removeInputError = (name: string) => setInputError(prevState => [...prevState].filter(input => input !== name));
  const removeMultiFieldError = () => setMultiFieldError(undefined);
  const setSubmitted = () => setHasSubmitted(() => true);
  const toggleDisableButton = (value?: boolean) => {
    if (inputErrors.length === 0) {
      if (value !== undefined) {
        setButtonDisabledStatus(value);
      } else {
        setButtonDisabledStatus(prev => !prev);
      }
    }
  };

  const addInputValidator: IManageForm['addInputValidator'] = (name, validator) => {
    inputValidators.current[name] = validator;
  };

  const removeInputValidator: IManageForm['removeInputValidator'] = name => {
    delete inputValidators.current[name];
  };

  const addCustomerValueGetter: IManageForm['addCustomerValueGetter'] = (name, getter) => {
    customValueGetter.current[name] = getter;
  };

  const removeCustomerValueGetter: IManageForm['removeCustomerValueGetter'] = name => {
    delete customValueGetter.current[name];
  };

  const getInputValue = (name: string): string => {
    if (!FormRef.current) {
      return '';
    }

    if (customValueGetter.current[name]) {
      return customValueGetter.current[name]();
    }

    const formData = new FormData(FormRef.current);

    const values = formData.getAll(name);

    return values.filter(v => typeof v === 'string').join(',');
  };

  const focusOnInput = (fieldName: string) => {
    const inputElement = document.querySelector<HTMLInputElement>(`[name="${fieldName}"]`);
    inputElement?.focus();
  };

  /**
   * All inputs which have added validators will run their validateValue function. Based on the
   * results, the first invalid input will be focused
   */
  const validateForm: IManageForm['validateForm'] = async () => {
    if (!FormRef.current) {
      return false;
    }

    const validators = Object.entries(inputValidators.current);

    let isFormValid = true;

    // https://maximorlov.com/linting-rules-for-asynchronous-code-in-javascript/#2-no-await-in-loop
    // eslint-disable-next-line no-restricted-syntax
    for await (const [name, validator] of validators) {
      const fieldValue = getInputValue(name);

      // Radio groups return null when not selected so we need to allow this as well as string
      if (typeof fieldValue !== 'string' && fieldValue !== null) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const isFieldValid = await validator(fieldValue, true);

      // We only want to focus on the first field, so, the first time we find an invalid field
      // mark the form, isFormValid, as not valid and focus on the field
      if (isFormValid && !isFieldValid) {
        isFormValid = false;
        focusOnInput(name);
      }
    }

    return isFormValid;
  };

  const focusFirstInput: IManageForm['focusFirstInput'] = () => {
    const firstInputName = Object.keys(inputValidators.current)[0];
    if (!firstInputName) {
      return;
    }

    focusOnInput(firstInputName);
  };

  const getData: IManageForm['getData'] = () => {
    if (!FormRef.current) {
      return {};
    }

    const fieldNames = Object.keys(inputValidators.current);

    const data: Record<string, string> = {};
    fieldNames.forEach(name => {
      data[name] = getInputValue(name);
    });

    return data;
  };

  /**
   *
   * @param inputs
   * @returns formData as an Object or undefined if inputs are missing
   *
   * This function takes in an array of strings that correspond with your required input id's, which should also match the contract
   * with the API.
   *
   * it will then return either:
   * 1. undefined  if there's an input unfilled, which can be used to ensure
   * the submit is stopped before hitting an API with incorrect data.
   * 2. a data object that contains all of the form data with the keys used in the array
   *
   * It does not do any complex data, if your inputs don't match the required data keys, you will need to do that yourself.
   */
  const getFormData: IManageForm['getFormData'] = inputs => {
    if (!FormRef.current) {
      return;
    }

    const formData = new FormData(FormRef.current);
    let data: any;

    if (!inputs.every(el => formData?.get(el))) {
      addFormInError();
      return data;
    }

    data = {};

    inputs.forEach(input => {
      data[input] = formData?.get(input);
    });
    return data;
  };

  const manageForm: IUseForm['manageForm'] = {
    addCustomerValueGetter,
    addFormInError,
    addInputError,
    addInputValidator,
    focusFirstInput,
    getData,
    getFormData,
    multiFieldError,
    removeCustomerValueGetter,
    removeFormInError,
    removeInputError,
    removeInputValidator,
    removeMultiFieldError,
    setMultiFieldError,
    setSubmitted,
    toggleDisableButton,
    validateForm
  };

  return { FormRef, errors, buttonDisabled, hasSubmitted, manageForm };
};
